import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetYTDHistoryEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { IYTDHistory } from '@nxsys/types/models/employee';
import { IRELAND } from '@nxsys/store/constants/keys';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { toNumber } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type Props = {
  employeeId: string;
};

const YTDHistoryTab: React.FC<Props> = ({ employeeId }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const dispatch = useDispatch<AppDispatch>();

  const { ytdHistoryList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list, pagination, queryParams } = ytdHistoryList;

  useEffect(() => {
    handleFetchData({ page: 0, size: 20 });
  }, []);

  const handleFetchData = (params: GetYTDHistoryEmployeeListParams) => {
    const newParams = { ...params, id: employeeId };
    dispatch(employeeActions.getYtdHistoryEmployeeList(newParams));
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  // Shared columns (both UK and Ireland)
  const sharedColumnsStart: ColumnsType<IYTDHistory> = [
    {
      key: 'createdAt',
      title: messages['common.At'],
      dataIndex: 'createdAt',
      width: 140,
      render: (value) => (
        <div>
          <div>{moment(value).format('DD/MM/YYYY')}</div>
          <span className="text-xs text-gray-500">
            {moment(value).format('hh:mm A')}
          </span>
        </div>
      ),
    },
    {
      key: 'reason',
      title: messages['common.reason'],
      dataIndex: 'reason',
      width: 250,
    },
    {
      key: 'employmentId',
      title: messages['common.employmentId'],
      dataIndex: 'employmentId',
      width: 180,
    },
    {
      key: 'taxPeriod',
      title: messages['table.taxPeriod'],
      width: 180,
      render: (value) =>
        value?.taxWeek ? `${value?.taxWeek}/${value?.taxYear}` : value?.taxYear,
    },
    {
      key: 'tax',
      title: messages['common.tax'],
      dataIndex: 'ytdValue',
      width: 180,
      render: (value) =>
        value.tax
          ? formatMoney(
              toNumber(value.tax),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'grossForTax',
      title: messages['common.grossForTax'],
      dataIndex: 'ytdValue',
      width: 180,
      render: (value) =>
        value.grossForTax
          ? formatMoney(
              toNumber(value.grossForTax),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
  ];

  // UK-only columns
  const ukColumns: ColumnsType<IYTDHistory> = [
    {
      key: 'eeNI',
      dataIndex: 'ytdValue',
      title: messages['common.employeeNI'],
      width: 180,
      render: (value) =>
        value.eeNI
          ? formatMoney(
              toNumber(value.eeNI),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'grossForNI',
      dataIndex: 'ytdValue',
      title: messages['common.grossForNI'],
      width: 180,
      render: (value) =>
        value.grossForNI
          ? formatMoney(
              toNumber(value.grossForNI),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'erNI',
      dataIndex: 'ytdValue',
      title: messages['common.employerNI'],
      width: 180,
      render: (value) =>
        value.erNI
          ? formatMoney(
              toNumber(value.erNI),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'stLoan',
      dataIndex: 'ytdValue',
      title: messages['common.studentLoan'],
      width: 180,
      render: (value) =>
        value.stLoan
          ? formatMoney(
              toNumber(value.stLoan),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
  ];

  // Ireland-only columns
  const irelandColumns: ColumnsType<IYTDHistory> = [
    {
      key: 'eePRSI',
      dataIndex: 'ytdValue',
      title: messages['common.employeePrsi'],
      width: 180,
      render: (value) =>
        value.eePRSI
          ? formatMoney(
              toNumber(value.eePRSI),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'erPRSI',
      dataIndex: 'ytdValue',
      title: messages['common.employerPrsi'],
      width: 180,
      render: (value) =>
        value.erPRSI
          ? formatMoney(
              toNumber(value.erPRSI),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'uscGross',
      dataIndex: 'ytdValue',
      title: messages['common.uscAbleGross'],
      width: 180,
      render: (value) =>
        value.uscGross
          ? formatMoney(
              toNumber(value.uscGross),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'usc',
      dataIndex: 'ytdValue',
      title: messages['common.usc'],
      width: 180,
      render: (value) =>
        value.usc
          ? formatMoney(
              toNumber(value.usc),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'lptDeduction',
      dataIndex: 'ytdValue',
      title: messages['common.lpt'],
      width: 180,
      render: (value) =>
        value.lptDeduction
          ? formatMoney(
              toNumber(value.lptDeduction),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
  ];

  // Shared columns at the end (both UK and Ireland)
  const sharedColumnsEnd: ColumnsType<IYTDHistory> = [
    {
      key: 'eePension',
      dataIndex: 'ytdValue',
      title: messages['common.employeePension'],
      width: 180,
      render: (value) =>
        value.eePension
          ? formatMoney(
              toNumber(value.eePension),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    ...(!isIreland
      ? [
          {
            key: 'pgLoan',
            dataIndex: 'ytdValue',
            title: messages['common.postGraduateStudentLoan'],
            width: 250,
            render: (value: any) =>
              value.pgLoan
                ? formatMoney(
                    toNumber(value.pgLoan),
                    showCurrencyByCountry(payrollCountry),
                  )
                : '-',
          },
        ]
      : []),
    {
      key: 'erPension',
      dataIndex: 'ytdValue',
      title: messages['common.employerPension'],
      width: 180,
      render: (value) =>
        value.erPension
          ? formatMoney(
              toNumber(value.erPension),
              showCurrencyByCountry(payrollCountry),
            )
          : '-',
    },
    {
      key: 'updatedByName',
      title: messages['common.updatedBy'],
      dataIndex: 'updatedByName',
      width: 180,
    },
  ];

  const columns: ColumnsType<IYTDHistory> = [
    ...sharedColumnsStart,
    ...(isIreland ? irelandColumns : ukColumns),
    ...sharedColumnsEnd,
  ];

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.ytdHistory']}
      </Typography.Title>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 2500, y: 700 }}
          columns={columns}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
    </div>
  );
};

export default YTDHistoryTab;
